import React, { ReactElement, useEffect, useState } from 'react'
import style from './style.less'
import Grid from '@yaak/components/src/Grid'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Toast from '@yaak/components/src/Toast'
import { toastType } from '@yaak/components/src/Toast/Toast'
import { useNavigate } from 'react-router-dom'
import Typography from '@yaak/components/src/Typography'
import { TypographyTypes } from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import { useSmallScreenMatches } from '../../customHooks/useSmallScreenMatches'
import Empty from '../Empty'
import {
  getMappedDrives,
  HEADERS,
  NUTRON_HEADERS,
} from './drives'
import {
  getActiveSessions,
  getSessions,
  Session,
} from '@yaak/components/services/api/api'
import SearchFilter from '../SearchFilter'
import {getLicenseLabel} from "../../helpers/licenseHelpers"
import {getLicenseTypes, LicenseType} from "../../services/api/api"
import { FormattedMessage} from 'react-intl'
import {useAuth0} from "@auth0/auth0-react";
import {getBcp47Locale} from "@yaak/ds-admin/src/helpers/helpers";

const headerSort = [
  'Time',
  'Partner',
  'Type',
  'Vehicle',
  'Kit ID',
  'Driver',
  'Instructor',
  'License',
]

const sortValue = [
  'startTimestamp',
  'partnerName',
  'driveType',
  'vin',
  'dongleId',
  'driverName',
  'instructorName',
  'licenseId',
]

const headerSortNutron = ['Type', 'Kit ID']

const sortValueNutron = ['driveType', 'dongleId']

interface SessionFilter {
  partnerId?: string
  userId?: string
  searchQuery?: string
  vin?: string
  dongleId?: string
  driveType?: number
  licenseId?: string
}

interface DrivesOverviewProps {
  token: string
  filter?: SessionFilter
  withHeader?: boolean
  withInstructor?: boolean
  withPartner?: boolean
  withKit?: boolean
  dsAdmin?: boolean
  withDriver?: boolean
  withVehicle?: boolean
  withVehicleName?: boolean
  fixedColumns?: number
  nutron?: boolean
  onClick?: (sessionId: string) => void
  showLicenseFilter?: boolean
  licenseOptions?: Array<{ value: string, label: string }>
}

interface dateRange {
  sessionStart: string | undefined
  sessionEnd: string | undefined
}

const DrivesOverview: React.FunctionComponent<DrivesOverviewProps> = ({
  token,
  filter = {},
  withHeader = true,
  withInstructor = true,
  withPartner = true,
  withKit = true,
  withDriver = true,
  withVehicle = true,
  withVehicleName = true,
  fixedColumns = 0,
  dsAdmin = false,
  nutron = false,
  onClick,
  showLicenseFilter = false,
  licenseOptions,
}): ReactElement => {
  const [loading, setLoading] = useState(true)
  const [sessions, setSessions] = useState<Session>({} as Session)
  const [mappedDrives, setMappedDrives] = useState<any>()
  const [activeSessions, setActiveSessions] = useState<Session>({} as Session)
  const [toast, setShowToast] = useState<toastType | null>(null)
  const [fetchMoreData, setFetchMoreData] = useState<boolean>(false)
  const [sortOrder, setSortOrder] = useState<string>('DESC')
  const [sortBy, setSortBy] = useState<string>()
  const [searchQuery, setSearchQuery] = useState('')
  const [licenseId, setLicenseId] = useState('')
  const [dateRange, setDateRange] = useState<dateRange | null>({
    sessionStart: undefined,
    sessionEnd: undefined,
  })
  const smallScreenMatches = useSmallScreenMatches()
  const [isClientSideSort, setIsClientSideSort] = useState(false)
  const [clientSortOrder, setClientSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [filterState, setFilterState] = useState<SessionFilter>(filter)
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])

  const { user } = useAuth0()
  const locale = getBcp47Locale(user)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchLicenseTypes = async () => {
      try {
        const types = await getLicenseTypes({ token, onAlert: setShowToast })
        setLicenseTypes(types)
      } catch (error) {
        console.error('Failed to fetch license types:', error)
      }
    }
    if (token) {
      fetchLicenseTypes()
    }
  }, [token])

  useEffect(() => {
    setFilterState((prev) => ({ ...prev, licenseId: licenseId || undefined }))
  }, [licenseId])

  useEffect(() => {
    const fetchSessions = async () => {
      const effectiveSortBy = isClientSideSort ? undefined : sortBy

      const sessions = await getSessions({
        token,
        sortBy: effectiveSortBy,
        sortOrder,
        partnerId: filterState.partnerId,
        userId: filterState.userId,
        searchQuery: filterState.searchQuery || searchQuery,
        vin: filterState.vin,
        dongleId: filterState.dongleId,
        driveType: filterState.driveType,
        licenseId: filterState.licenseId,
        ...dateRange,
        onAlert: setShowToast,
      })

      const activeSessions = await getActiveSessions({
        token,
        onAlert: setShowToast,
      })
      setActiveSessions(activeSessions)

      if (isClientSideSort && sessions?.data?.length > 0) {
        const sortedData = [...sessions.data].sort((a, b) => {
          const aLicenseId = a.licenseId as any
          const bLicenseId = b.licenseId as any

          const labelA = getLicenseLabel(aLicenseId, licenseTypes) || ''
          const labelB = getLicenseLabel(bLicenseId, licenseTypes) || ''

          if (clientSortOrder === 'ASC') {
            return labelA.localeCompare(labelB)
          }
          return labelB.localeCompare(labelA)
        })

        setSessions({
          ...sessions,
          data: sortedData
        })
      } else {
        setSessions(sessions)
      }

      setLoading(false)
    }

    const debounceTimeout = setTimeout(() => {
      token && fetchSessions()
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [token, sortBy, sortOrder, filterState, searchQuery, dateRange, isClientSideSort, clientSortOrder])

  useEffect(() => {
    if (sessions?.data?.length > 0) {
      const mappedDrives = getMappedDrives({
        sessionsData: sessions.data,
        activeSessionsData: activeSessions?.data,
        setShowToast,
        totalCount: sessions.metaData.totalCount,
        matches: smallScreenMatches,
        navigate,
        licenseTypes,
        options: {
          withPartner,
          withInstructor,
          fixedColumns,
          withKit,
          dsAdmin,
          withDriver,
          withVehicle,
          nutron,
          withVehicleName
        },
      }, locale)
      setMappedDrives(mappedDrives)
    }
  }, [sessions?.data, activeSessions?.data, smallScreenMatches, licenseTypes])

  useEffect(() => {
    fetchMoreData && fetchData2()
    setFetchMoreData(false)
  }, [fetchMoreData])

  const fetchData = () => {
    setFetchMoreData(true)
  }

  const fetchData2 = async () => {
    const morePages =
      sessions.data.length &&
      sessions.data.length < sessions.metaData.totalCount

    if (morePages) {
      const newSessionPage = await getSessions({
        token,
        onAlert: setShowToast,
        offset: sessions.data.length,
        sortBy,
        sortOrder,
        searchQuery,
        partnerId: filterState.partnerId,
        userId: filterState.userId,
        vin: filterState.vin,
        dongleId: filterState.dongleId,
        driveType: filterState.driveType,
        licenseId: filterState.licenseId,
      })
      if (newSessionPage) {
        setSessions({
          ...sessions,
          data: [...sessions.data, ...newSessionPage.data],
        })
      }
    }
  }

  const onRowClick = (rowIndex: number) => {
    !nutron
      ? !dsAdmin && navigate(`/drives/${sessions.data[rowIndex].id}`)
      : onClick?.(sessions.data[rowIndex].id)
  }

  const onHeaderClicked = (props: any) => {
    if (props.sortOrder[props.index] !== null) {
      const clickedColumn = nutron ? sortValueNutron[props.index] : sortValue[props.index]

      if (clickedColumn === 'licenseId') {
        setIsClientSideSort(true)
        setClientSortOrder(props.sortOrder[props.index] ? 'DESC' : 'ASC')
        setSortBy(clickedColumn)
        setSortOrder(props.sortOrder[props.index] ? 'DESC' : 'ASC')
      } else {
        setIsClientSideSort(false)
        setSortBy(clickedColumn)
        setSortOrder(props.sortOrder[props.index] ? 'DESC' : 'ASC')
      }
    }
  }

  const headersAsStrings = nutron
    ? NUTRON_HEADERS.map((header) => typeof header === 'string' ? header : 'Header')
    : HEADERS.map((header) => typeof header === 'string' ? header : 'Header')

  return (
    <div className={withHeader ? style.overview : style.overviewWithoutHeader}>
      <Typography
        type={TypographyTypes.headline}
        className={!withHeader ? style.hide : undefined}
      >
        {'Drives'}
      </Typography>
      <Divider className={!withHeader ? style.hide : undefined} />
      <SearchFilter
        setSearchQuery={setSearchQuery}
        setDateRange={setDateRange}
        licenseId={licenseId}
        setLicenseId={setLicenseId}
        showLicenseFilter={showLicenseFilter}
        licenseOptions={licenseOptions}
      />
      <div
        className={withHeader ? style.gridContainer : style.userGridContainer}
      >
        {loading ? (
          <ProgressBar />
        ) : sessions?.data?.length > 0 && mappedDrives?.rows?.length > 0 ? (
          <Grid
            data={mappedDrives}
            fetchData={fetchData}
            headerSort={nutron ? headerSortNutron : headerSort}
            onHeaderClicked={onHeaderClicked}
            onRowClick={onRowClick}
            headers={headersAsStrings}
          />
        ) : searchQuery ? (
          <Empty
            header={<FormattedMessage id="no_drives_search" />}
            content={<FormattedMessage id="no_drives_search_content" />}
          />
        ) : (
          <Empty
            header={<FormattedMessage id="no_drives" />}
            content={<FormattedMessage id="no_drives_content" />}
          />
        )}
      </div>
      {toast && <Toast toast={toast} setShowToast={setShowToast} />}
    </div>
  )
}

export default DrivesOverview
